import { Link } from "react-router-dom";
import {
  BadgeCheck, ClipboardCheck, Coins, Eye, HandHeart, Lock, Search, ShieldCheck, Stethoscope, UserCheck,
} from "lucide-react";
import { SiteFooter, SiteNav } from "@/components/SiteChrome";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const IMG = {
  hero: "https://images.pexels.com/photos/4989148/pexels-photo-4989148.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  nurse: "https://images.pexels.com/photos/7551683/pexels-photo-7551683.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  elderly: "https://images.pexels.com/photos/7551674/pexels-photo-7551674.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  consult: "https://images.pexels.com/photos/5234519/pexels-photo-5234519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const STEPS = [
  { icon: UserCheck, title: "Verify your identity", body: "Register, confirm your phone by OTP and submit government ID with a biometric selfie." },
  { icon: Search, title: "Find a verified professional", body: "Browse licensed doctors, nurses and physiotherapists checked against MDCN, NMCN and MRTBN records." },
  { icon: HandHeart, title: "Care delivered at home", body: "Care is arranged for your named beneficiary, with a witness on record for accountability." },
];

const PROVIDERS = [
  { name: "Dr. Emeka Nwosu", cat: "General Practitioner", body: "MDCN", state: "Lagos", img: IMG.hero },
  { name: "Nurse Blessing Etim", cat: "Home Care Nurse", body: "NMCN", state: "Abuja", img: IMG.nurse },
  { name: "Adaeze Physio Team", cat: "Physiotherapy", body: "MRTBN", state: "Lagos", img: IMG.consult },
];

const FAQS = [
  ["Why does CARENEST require government ID and a selfie?", "Care happens inside your home. Verifying every client and every professional against a government ID and a live selfie keeps vulnerable beneficiaries safe and creates an auditable record for every visit."],
  ["How is my money protected?", "Funds for a booking are held in escrow and only released after the visit is confirmed. Escrow funding and release go live in Stage 2."],
  ["What does a witness do?", "A witness is a trusted person — family, neighbour or community member — recorded against a care visit. They confirm a professional attended and behaved appropriately. Formal KYC is not required for witnesses."],
  ["Are the professionals really licensed?", "Every provider submits a professional licence photograph, licence serial number and MDCN/NMCN/MRTBN registration details. Our team reviews the documents before the profile becomes visible in the marketplace."],
  ["Who can see my medical information?", "Beneficiary medical details and KYC documents are stored privately and only accessible to you and authorised reviewers through short-lived signed links. Nothing sensitive is served from a public URL."],
  ["How does the referral programme work?", "Referrers complete a light KYC, declare their relationship to the people they refer and receive a unique referral code. Referral bonuses and payouts arrive in Stage 3."],
];

const Section = ({ id, eyebrow, title, lead, children, className = "" }) => (
  <section id={id} className={`py-16 sm:py-20 lg:py-24 ${className}`}>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">{eyebrow}</p>
      <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
        {title}
      </h2>
      {lead && <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{lead}</p>}
      <div className="mt-10 sm:mt-12">{children}</div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-sand">
      <SiteNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-emerald-900 cn-grain">
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:py-24">
          <div className="cn-rise lg:col-span-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-700 bg-emerald-800/60 px-4 py-1.5 text-xs font-semibold text-emerald-100">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified. Escrow-protected. Witnessed.
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Trusted Healthcare,
              <span className="block text-emerald-300">Delivered to Your Home</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-emerald-100/85 sm:text-lg">
              CARENEST connects Nigerian families with licensed doctors, nurses and therapists — every professional
              identity-checked, every payment held in escrow, every visit witnessed.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-white px-7 py-6 text-emerald-900 hover:bg-emerald-50">
                <Link to="/register" data-testid="hero-get-started-btn">Create your account</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-emerald-600 bg-transparent px-7 py-6 text-emerald-50 hover:bg-emerald-800"
              >
                <Link to="/register" data-testid="hero-provider-btn">Join as a professional</Link>
              </Button>
            </div>
            <dl className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-emerald-800 pt-7">
              {[["ID + biometric", "verification"], ["Escrow", "held funds"], ["Witness", "accountability"]].map(([a, b]) => (
                <div key={a}>
                  <dt className="font-display text-base font-bold text-white">{a}</dt>
                  <dd className="text-xs text-emerald-300">{b}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="cn-rise lg:col-span-6">
            <div className="relative">
              <img
                src={IMG.nurse}
                alt="Nurse providing home care to a patient in Nigeria"
                className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
              />
              <div className="absolute -bottom-6 left-4 flex items-center gap-3 rounded-2xl border border-emerald-100 bg-white p-4 shadow-xl sm:left-8">
                <BadgeCheck className="h-9 w-9 text-emerald-600" />
                <div>
                  <p className="font-display text-sm font-bold text-slate-900">Licence verified</p>
                  <p className="text-xs text-slate-500">MDCN · NMCN · MRTBN checks</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Section id="how-it-works" eyebrow="How CARENEST works" title="Three steps from worry to care at the door" lead="Built for families arranging care for a parent, a child or a relative in another city.">
        <div className="grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-transform hover:-translate-y-1">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-900 text-emerald-50">
                <s.icon className="h-5 w-5" />
              </span>
              <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">Step {i + 1}</p>
              <h3 className="mt-1.5 font-display text-lg font-bold text-slate-900">{s.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="providers" eyebrow="Verified healthcare providers" title="Only professionals who pass verification appear" lead="Unverified or suspended profiles never reach the marketplace." className="bg-white">
        <div className="grid gap-6 md:grid-cols-3">
          {PROVIDERS.map((p) => (
            <article key={p.name} className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
              <img src={p.img} alt={p.name} className="h-52 w-full object-cover" />
              <div className="p-6">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  <BadgeCheck className="h-3.5 w-3.5" /> Verified · {p.body}
                </span>
                <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{p.name}</h3>
                <p className="text-sm text-slate-500">{p.cat} · {p.state}</p>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-8 text-sm text-slate-500">
          Sign in as a KYC-verified client to browse the live marketplace with fees and availability.
        </p>
      </Section>

      <Section id="safety" eyebrow="Safety & verification" title="Nobody enters your home unverified">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            [ClipboardCheck, "Government ID", "NIN, passport, driver's licence or voter's card, captured and reviewed by our team."],
            [Eye, "Biometric selfie", "A live selfie is matched against the submitted ID document."],
            [Stethoscope, "Licence checks", "Licence serial numbers and MDCN/NMCN/MRTBN registrations reviewed before listing."],
            [Lock, "Private documents", "KYC files never sit on public URLs — access is via short-lived signed links only."],
          ].map(([Icon, t, b]) => (
            <div key={t} className="rounded-2xl border border-emerald-500/20 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(5,150,105,0.1)]">
              <Icon className="h-6 w-6 text-emerald-700" />
              <h3 className="mt-4 font-display font-bold text-slate-900">{t}</h3>
              <p className="mt-2 text-sm text-slate-600">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <section id="escrow" className="bg-emerald-900 py-16 sm:py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-300">Escrow protection</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Your money moves only after care is delivered
            </h2>
            <p className="mt-5 max-w-xl text-emerald-100/85">
              Every client accepts the CARENEST Escrow Terms during onboarding. Booking funds are held by a licensed
              escrow partner and released to the professional after the visit is confirmed by the client and the witness.
            </p>
            <ul className="mt-7 space-y-3 text-sm text-emerald-100/90">
              {["Funds held, never paid upfront to strangers", "Dispute window before release", "Witness confirmation on record"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <Coins className="h-4 w-4 shrink-0 text-emerald-300" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <img src={IMG.elderly} alt="Elderly patient receiving care at home" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" />
        </div>
      </section>

      <Section id="witness" eyebrow="Witness verification" title="A trusted person on record for every visit" lead="Witnesses need only a name, phone number, photograph and address — no formal KYC." className="bg-white">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            ["Added by the family", "Master Clients add, edit or remove witnesses from their dashboard at any time."],
            ["Present at the visit", "The witness confirms the professional attended and care was delivered respectfully."],
            ["Part of the audit trail", "Every witness change is written to the CARENEST audit log."],
          ].map(([t, b]) => (
            <div key={t} className="rounded-2xl border border-slate-200/80 bg-sand p-7">
              <h3 className="font-display font-bold text-slate-900">{t}</h3>
              <p className="mt-2 text-sm text-slate-600">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="referrals" eyebrow="Referral programme" title="Help a family find care, build a reputation">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm">
            <p className="text-sm text-slate-600">
              Individuals, business representatives and group representatives can register as CARENEST Referrers,
              complete a baseline KYC, declare their relationship to the people they support and generate a unique
              referral code.
            </p>
            <div className="mt-6 rounded-xl border border-dashed border-emerald-300 bg-emerald-50 p-5 font-mono text-sm text-emerald-800">
              CN-DEMO001
            </div>
            <p className="mt-4 text-xs text-slate-500">Referral bonuses and payouts are enabled in Stage 3.</p>
            <Button asChild className="mt-6 rounded-full bg-emerald-900 px-6 hover:bg-emerald-800">
              <Link to="/register" data-testid="referral-cta-btn">Become a referrer</Link>
            </Button>
          </div>
          <img src={IMG.consult} alt="Healthcare consultant" className="h-full w-full rounded-2xl object-cover" />
        </div>
      </Section>

      <Section id="faq" eyebrow="FAQ" title="Questions Nigerian families ask us" className="bg-white">
        <Accordion type="single" collapsible className="max-w-3xl" data-testid="faq-accordion">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={q} value={`item-${i}`}>
              <AccordionTrigger className="text-left font-display font-semibold text-slate-900">{q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <SiteFooter />
    </div>
  );
}
